import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Drawer, LinearProgress, Paper } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSession } from 'components/Providers'
import { retrieve, search } from 'store/member/contact'
import { cancel, options as searchOptions, search as searchRequests } from 'store/academic/parent/address-change'
import PageHeader from 'components/AppBar/PageHeader'
// import Header from 'components/Header'
import NoRecords from 'components/NoRecords'
import ContactsList from './List'
import AddressChangeRequestCard from './List/AddressChangeRequestCard'
import ContactsFormIndex from './Form'
import AddressChangeRequestForm from './Form/AddressChangeRequestForm'
import * as snackbar from 'utils/snackbar'

export default function ParentContacts() {
  const dispatch = useDispatch()
  const { person } = useSession()
  // const type = person?.type
  const { t } = useTranslation()

  const [data, setData] = useState(null)
  const [editing, setEditing] = useState(null)
  const [addressChange, setAddressChange] = useState(null)
  const [addressOptions, setAddressOptions] = useState(null)
  const [requests, setRequests] = useState(null)

  const memberId = useSelector(
    state => state.academic?.parentStudent?.selectedStudent?.personAccessViews?.[0]?.id
  )

  const load = useCallback(() => {
    if (!memberId) return

    dispatch(search({ type: 'student', memberId }))
      .then(res => {
        setData(res?.payload || [])
      })
      .catch(error => {
        console.log('contacts error:', error)
        setData([])
      })
  }, [dispatch, memberId])

  useEffect(() => {
    if (!memberId) return
    setData(null)
    load()
  }, [memberId, load])

  const loadAddressChange = useCallback(async () => {
    if (!memberId) return

    const [optionsRes, requestsRes] = await Promise.all([
      dispatch(searchOptions()),
      dispatch(searchRequests({ memberId })),
    ])
    setAddressOptions(optionsRes.error ? false : optionsRes.payload)
    setRequests(requestsRes.error ? [] : requestsRes.payload)
  }, [dispatch, memberId])

  useEffect(() => {
    if (!memberId) return
    setAddressOptions(null)
    setRequests(null)
    loadAddressChange()
  }, [memberId, loadAddressChange])

  const closeDrawer = () => setEditing(null)

  const refreshContact = async id => {
    const res = await dispatch(retrieve({ type: 'student', memberId, id }))
    if (res.error || !res.payload) return load()
    setData(prev => prev?.map(item => (String(item.id) === String(id) ? res.payload : item)))
  }

  const onSaved = () => {
    const saved = editing
    setEditing(null)
    if (saved?.own && saved.contact?.id) refreshContact(saved.contact.id)
    else load()
  }

  const closeAddressChange = () => setAddressChange(null)

  const onAddressChangeDone = () => {
    closeAddressChange()
    loadAddressChange()
  }

  const cancelRequest = async request => {
    const res = await dispatch(cancel({ memberId, id: request.id }))
    if (!res.error) snackbar.success(t('Address change request cancelled'))
    loadAddressChange()
  }

  const latestRequest = requests?.[0]
  const loadingAddressChange = addressOptions === null || requests === null || data === null
  const canRequestAddressChange = Boolean(addressOptions) && latestRequest?.status !== 'pending'
  const ownContact = data?.find(item => String(item?.contact?.id) === String(person?.id))
  const isPrimaryParent = Boolean(addressOptions) && ownContact?.isGuardian === true && Number(ownContact.seq) === 1

  if (!memberId) {
    return (
      <Box sx={{ minHeight: '50%' }}>
        <Box sx={{ pb: 2 }}>
          <PageHeader title={'Contacts'} />
        </Box>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <NoRecords
            message={t('No student is selected yet')}
            description={t('Select a student from the menu on the left to see their contacts.')}
          />
        </Paper>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '50%' }}>
      <Box sx={{ pb: 2 }}>
        <PageHeader
          title={'Contacts'}
        />
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {/* <Header
          title={
            type === 'parent'
              ? `${parentStudent?.firstName ?? ''} ${parentStudent?.lastName ?? ''}'s Contacts`
              : `${person?.firstName ?? ''} ${person?.lastName ?? ''}`
          }
          small
        /> */}

        {(loadingAddressChange || isPrimaryParent) && (
          <AddressChangeRequestCard
            loading={loadingAddressChange}
            request={latestRequest}
            canRequest={canRequestAddressChange}
            onRequest={() => setAddressChange({})}
            onEdit={addressOptions ? request => setAddressChange({ request }) : undefined}
            onCancel={cancelRequest}
          />
        )}

        {!data ? (
          <LinearProgress />
        ) : (
          <ContactsList
            data={data}
            memberId={memberId}
            onEdit={contact => setEditing({ contact })}
            onEditOwn={({ contact, isPrimary }) => setEditing({ own: true, contact, isPrimary })}
            onAdd={seq => setEditing({ seq })}
            onReordered={load}
          />
        )}
      </Paper>

      <Drawer
        PaperProps={{ sx: { width: { xs: '100%', sm: 640 } } }}
        open={Boolean(editing)}
        anchor="right"
        onClose={closeDrawer}
      >
        {editing && (
          <ContactsFormIndex
            memberId={memberId}
            contact={editing.contact}
            seq={editing.seq}
            own={editing.own}
            isPrimary={editing.isPrimary}
            onClose={closeDrawer}
            onSaved={onSaved}
          />
        )}
      </Drawer>

      <Drawer
        PaperProps={{ sx: { width: { xs: '100%', sm: 640 } } }}
        open={Boolean(addressChange)}
        anchor="right"
        onClose={closeAddressChange}
      >
        {addressChange && (
          <AddressChangeRequestForm
            memberId={memberId}
            options={addressOptions}
            request={addressChange.request}
            contacts={data}
            onClose={closeAddressChange}
            onSaved={onAddressChangeDone}
            onConflict={onAddressChangeDone}
          />
        )}
      </Drawer>
    </Box>
  )
}
